import React, { useState } from 'react';

interface FooterProps {
  /**
   * Optional additional CSS classes for styling customization
   */
  className?: string;
  /**
   * Whether to show the footer (useful for conditional rendering)
   */
  show?: boolean;
}

/**
 * Enterprise-level Footer component with "Powered by" branding
 * 
 * Features:
 * - Responsive design with TailwindCSS
 * - Dark mode support
 * - Accessibility compliant (WCAG 2.1)
 * - Smooth hover animations
 * - Error handling for image loading
 * - TypeScript support
 * - SEO-friendly external linking
 */
const Footer: React.FC<FooterProps> = ({ 
  className = '', 
  show = true 
}) => {
  const [imageError, setImageError] = useState<boolean>(false);

  // Don't render if show is false
  if (!show) return null;

  const handleImageError = () => {
    setImageError(true);
  };

  const handleImageLoad = () => {
    setImageError(false);
  };

  return (
    <footer 
      className={`
        w-full 
        border-t border-gray-200 dark:border-gray-700
        bg-gray-50/50 dark:bg-gray-900/50
        backdrop-blur-sm
        mt-auto
        ${className}
      `}
      role="contentinfo"
      aria-label="Site footer with branding information"
    >
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-center">
          <div className="flex items-center space-x-2 group">
            {/* "Powered by" text */}
            <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">
              Powered by
            </span>

            {/* External link to Architeqt */}
            <a
              href="https://architeqt.tech/"
              target="_blank"
              rel="noopener noreferrer"
              className="
                inline-flex items-center space-x-2
                text-base font-semibold
                text-gray-700 dark:text-gray-300
                hover:text-blue-600 dark:hover:text-blue-400
                transition-all duration-200 ease-in-out
                hover:scale-105
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                dark:focus:ring-offset-gray-900
                rounded-md px-2 py-1
                group-hover:bg-white/50 dark:group-hover:bg-gray-800/50
              "
              aria-label="Visit Architeqt website (opens in new tab)"
            >
              {/* Logo image with error handling */}
              {!imageError && (
                <img
                  src="/images/logo.png"
                  alt="Architeqt logo"
                  className="
                    w-6 h-6 
                    transition-transform duration-200 ease-in-out
                    group-hover:scale-110
                    object-contain
                  "
                  onError={handleImageError}
                  onLoad={handleImageLoad}
                  loading="lazy"
                />
              )}

              {/* Fallback icon if image fails to load */}
              {imageError && (
                <div 
                  className="
                    w-6 h-6 
                    bg-gradient-to-br from-blue-500 to-blue-600
                    rounded-sm
                    flex items-center justify-center
                    transition-transform duration-200 ease-in-out
                    group-hover:scale-110
                  "
                  aria-hidden="true"
                >
                  <span className="text-white text-xs font-bold">A</span>
                </div>
              )}

              {/* Company name */}
              <span className="tracking-wide">
                Architeqt
              </span>

              {/* External link indicator */}
              <svg
                className="w-3 h-3 opacity-60 group-hover:opacity-100 transition-opacity"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;